import ExtLink from './ExtLink';
import Stack from './Stack';
import Tooltip from './Tooltip';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useDataSourceVersion } from 'utils/hooks';
import { useSelector } from 'react-redux';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const DefinitionTitle = styled.strong`
    font-weight: bold;
    color: ${({ theme }) => theme.gray_7};
    display: block;
    text-transform: capitalize;
    margin-bottom: ${({ theme }) => theme.space_3}px;
`;

const SourceSection = styled.small`
    display: block;
    margin-top: ${({ theme }) => theme.space_5}px;
`;

const SourceLabel = styled.div`
    font-size: 0.75rem;
    font-weight: ${({ theme }) => theme.fontSemiBold};
    color: ${({ theme }) => theme.gray_3};
    margin-bottom: ${({ theme }) => theme.space_2}px;
`;

const SourceValue = styled.div`
    font-size: 0.75rem;
    line-height: 1.4;
    color: ${({ theme }) => theme.gray_3};
`;

const DefinitionBody = styled.div`
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontMedium};
    font-size: 0.75rem;
`;

const LearnMore = styled.div`
    margin-top: ${({ theme }) => theme.space_3}px;
    font-size: 0.75rem;
`;

function SourceAttribution({
    dbDate,
    countryTextId,
    isLoading,
    isError,
    formatAttribution,
}) {
    if (dbDate && countryTextId)
        return formatAttribution(dbDate, countryTextId);
    if (isLoading) return `Loading source info...`;
    if (
        isError ||
        typeof formatAttribution !== 'function' ||
        !dbDate ||
        !countryTextId
    ) {
        return `Could not get source info.`;
    }
    return null;
}

export function DefinitionTooltipContent({
    title,
    body,
    link,
    source,
    countries,
    sourceDataType = 'search',
    week,
    year,
}) {
    const { googleVersion, tiktokVersion, instagramVersion } =
        useDataSourceVersion();
    let isLoading = false;
    let isError = false;
    if (sourceDataType === 'search') {
        isLoading = googleVersion.isLoading;
        isError = googleVersion.isError;
    } else if ('tiktok' === sourceDataType) {
        isLoading = tiktokVersion.isLoading;
        isError = tiktokVersion.isError;
    } else if ('instagram' === sourceDataType) {
        isLoading = instagramVersion.isLoading;
        isError = instagramVersion.isError;
    }
    const currentCountry = useSelector(({ geo }) => geo);
    const textId = currentCountry?.text_id;

    // Weekly (tiktok/instagram) sources need a dbDate derived from week+year.
    // Fall back to the version query's per-country week/year when a caller
    // (e.g. a health-stat tooltip) doesn't have them in scope.
    let effWeek = week;
    let effYear = year;
    if (sourceDataType === 'tiktok') {
        effWeek = effWeek ?? tiktokVersion.data?.countries?.[textId]?.week;
        effYear = effYear ?? tiktokVersion.data?.countries?.[textId]?.year;
    } else if (sourceDataType === 'instagram') {
        effWeek = effWeek ?? instagramVersion.data?.countries?.[textId]?.week;
        effYear = effYear ?? instagramVersion.data?.countries?.[textId]?.year;
    }
    let weeklyDbDate = null;
    if (effWeek && effYear) {
        weeklyDbDate = dayjs()
            .week(effWeek)
            .year(effYear)
            .startOf('week')
            .format('YYYY-MM-DD');
    }
    let dbDate = null;

    const countryTextId =
        countries ||
        (currentCountry ? currentCountry.code || currentCountry.name : null);

    if (sourceDataType === 'search') {
        dbDate =
            googleVersion.data &&
            currentCountry &&
            currentCountry.text_id &&
            googleVersion.data?.dbDates?.[currentCountry.text_id]
                ? googleVersion.data?.dbDates?.[currentCountry.text_id]
                      .sourceEndDate
                : null;
    } else if (['tiktok', 'instagram'].includes(sourceDataType)) {
        dbDate = weeklyDbDate || null;
    }
    return (
        <>
            {title && <DefinitionTitle>{title}</DefinitionTitle>}
            {body && (
                <Stack $space=".5rem">
                    {Array.isArray(body) ? (
                        body.map((text) => (
                            <DefinitionBody key={text}>{text}</DefinitionBody>
                        ))
                    ) : (
                        <DefinitionBody>{body}</DefinitionBody>
                    )}
                </Stack>
            )}
            {link && (
                <LearnMore>
                    <ExtLink
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        mixpanelContext="DEFINITION_TOOLTIP_LINK"
                    >
                        Learn More
                    </ExtLink>
                </LearnMore>
            )}
            {source && (
                <SourceSection>
                    <SourceLabel>Source</SourceLabel>
                    <SourceValue>
                        <SourceAttribution
                            isLoading={isLoading}
                            isError={isError}
                            dbDate={dbDate}
                            countryTextId={countryTextId}
                            formatAttribution={source}
                        />
                    </SourceValue>
                </SourceSection>
            )}
        </>
    );
}

function DefinitionTooltip({
    children,
    title,
    body,
    link,
    source,
    countries,
    sourceDataType = 'search',
    week,
    year,
    ...restProps
}) {
    return (
        <Tooltip
            content={
                <DefinitionTooltipContent
                    title={title}
                    body={body}
                    link={link}
                    source={source}
                    countries={countries}
                    sourceDataType={sourceDataType}
                    week={week}
                    year={year}
                />
            }
            {...restProps}
        >
            {children}
        </Tooltip>
    );
}

export default DefinitionTooltip;
